import {
  ColumnDefinition,
  renderColumn,
  sortableColumn
} from 'components/TableColumn/TableColumn';
import { L1Object, L1ObjectChannel } from 'core/l1Object/L1Object';
import { Order } from 'core/order/Order';
import { useEffect, useMemo, useState } from 'react';
import styles from './l1ObjectBudgetManagement.module.scss';
import _ from 'lodash';
import formatters from './listFormatter';
import { formatPriceWithCurrency, getPriceValue } from 'helper/CurrencyHelper';
import { validateMinimum } from 'utils/ValidateUtils';
import i18n from 'i18n';
import {
  CampaignState,
  RtbCampaignListBasic
} from 'core/rtbCampaign/RtbCampaign';
import { useCallAPI } from 'hooks/useCallAPI';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';

export enum L1ObjectBudgetManagementColumns {
  NAME = 'id',
  STATUS = 'status',
  SPENT = 'spent',
  BUDGET = 'budget'
}

type StatusDesData = {
  des: string;
  color: string;
};

export type L2ObjectBudgetListRow = {
  channel: L1ObjectChannel;
  id: number;
  name: string;
  status: StatusDesData;
  spent: number;
  budget: number;
  minBudget: number;
  error?: string;
};

export const useL1ObjectBudgetManagementModel = (
  order: Order,
  l1Object: Partial<L1Object>,
  initL1Object: Partial<L1Object> | undefined,
  l2ObjectList: RtbCampaignListBasic[],
  budgetDataList?: { id: string; budget: number }[]
) => {
  const { loading, callAPIs } = useCallAPI();
  const [viewData, setViewData] = useState<L2ObjectBudgetListRow[]>([]);
  const [budgets, setBudgets] = useState<{ [key: number]: number }>({});

  useEffect(() => {
    callAPIs(
      [
        async () =>
          fetchRtbCampaignLifetimeSpents(
            order,
            l1Object,
            budgetDataList,
            l2ObjectList
          )
      ],
      ({ viewData, budgets }) => {
        setViewData(viewData);
        setBudgets(budgets);
      }
    );
  }, [order, l1Object, l2ObjectList, budgetDataList, callAPIs]);

  const columnDefinition = (columnName): ColumnDefinition => ({
    ...sortableColumn(
      columnName,
      `l1ObjectBudgetManagement.headers.${columnName}`,
      true
    ),
    classes: () => styles[columnName],
    headerClasses: () => styles[columnName]
  });

  const onBudgetChange = (id, newBudget) => {
    const index = viewData.findIndex((data) => data.id === id);
    if (index === -1) {
      return;
    }
    viewData[index].error = validateMinimum(
      newBudget,
      viewData[index].minBudget
    );
    // The row keeps the typed amount too, so that sorting by budget and the
    // value the input falls back to both follow what the operator entered.
    viewData[index].budget = +newBudget;
    setBudgets({
      ...budgets,
      [id]: +newBudget
    });
    setViewData([...viewData]);
  };

  const remainBudget = useMemo(() => {
    const budget =
      initL1Object && initL1Object.budget
        ? initL1Object.budget
        : order.budgetBalance;
    return (
      +budget -
      Object.keys(budgets).reduce((acc, key) => acc + +budgets[key], 0)
    );
  }, [order.budgetBalance, initL1Object, budgets]);

  const columns = [
    renderColumn<
      L2ObjectBudgetListRow,
      L2ObjectBudgetListRow[L1ObjectBudgetManagementColumns.NAME]
    >(
      {
        ...columnDefinition(L1ObjectBudgetManagementColumns.NAME),
        footer: ''
      },
      (_1, l2Object) => l2Object.name
    ),
    renderColumn<
      L2ObjectBudgetListRow,
      L2ObjectBudgetListRow[L1ObjectBudgetManagementColumns.STATUS]
    >(
      {
        ...columnDefinition(L1ObjectBudgetManagementColumns.STATUS),
        footer: ''
      },
      formatters.stateFormatter
    ),
    renderColumn<
      L2ObjectBudgetListRow,
      L2ObjectBudgetListRow[L1ObjectBudgetManagementColumns.SPENT]
    >(
      {
        ...columnDefinition(L1ObjectBudgetManagementColumns.SPENT),
        footer: ''
      },
      (value) => formatPriceWithCurrency(order.currency, value)
    ),
    renderColumn<
      L2ObjectBudgetListRow,
      L2ObjectBudgetListRow[L1ObjectBudgetManagementColumns.BUDGET],
      {
        currency: string;
        onChange: (id, newBudget) => void;
      }
    >(
      {
        ...columnDefinition(L1ObjectBudgetManagementColumns.BUDGET),
        formatExtraData: {
          currency: order.currency,
          onChange: onBudgetChange
        },
        footer: i18n.t(`l1ObjectBudgetManagement.hints.campaignBudget`, {
          total: remainBudget
        }),
        footerClasses: remainBudget < 0 ? styles.dangerHint : undefined
      },
      formatters.budgetFormatter
    )
  ];

  const canSubmit = useMemo(() => {
    let hasError = remainBudget < 0;
    viewData.forEach((data) => {
      if (data.error) {
        hasError = true;
      }
    });
    return !hasError;
  }, [remainBudget, viewData]);

  return {
    loading,
    viewData,
    columns,
    budgets,
    canSubmit
  };
};

const getRtbCampaignStatusDesData = (campaign): StatusDesData => {
  let des = '';
  let color;
  switch (campaign.state) {
    case CampaignState.DEACTIVATE:
      des = i18n.t('campaignList.labels.deactivateState');
      color = 'black';
      break;
    case CampaignState.DELETE:
      des = i18n.t('campaignList.labels.deleteState');
      color = 'danger';
      break;
    case CampaignState.ACTIVATE:
      des = i18n.t('campaignList.labels.activateState');
      break;
    default:
      des = 'In Process';
      color = 'whiteTheme4';
      break;
  }

  return {
    des,
    color
  };
};

export async function fetchRtbCampaignLifetimeSpents(
  order,
  l1Object,
  budgetDataList,
  l2ObjectList
): Promise<{
  viewData: L2ObjectBudgetListRow[];
  budgets: { [key: number]: number };
}> {
  let totalBudget = l1Object.budget;
  const currencyRate = _.get(l1Object, 'currencyRate', 1);
  const increaseBudgetToTarget = (origin: number, target: number) => {
    if (totalBudget <= 0) {
      return origin;
    }
    if (origin >= target) {
      return origin;
    }
    let amount = target - origin;
    if (totalBudget < amount) {
      amount = totalBudget;
    }
    totalBudget -= amount;
    return origin + amount;
  };
  try {
    const budgetDataMap = budgetDataList
      ? budgetDataList.reduce((acc, budget) => {
          return {
            ...acc,
            [budget.id]: budget.budget
          };
        }, {})
      : {};
    const spentMap = l2ObjectList.reduce((acc, l2Object) => {
      const spent =
        Math.max(
          _.defaultTo(l2Object.olapActualSpent, 0),
          _.defaultTo(l2Object.olapExpectSpent, 0)
        ) * currencyRate;
      acc[l2Object.id] = spent;
      return acc;
    }, {});

    const l2LevelNoRemainBudget = l2ObjectList.every((l2Object) => {
      return spentMap[l2Object.id] > l2Object.budget;
    });
    const canNotAssignBudget =
      l2ObjectList.reduce((acc, l2Object) => acc + l2Object.budget, 0) >=
      l1Object.budget;
    let initBudgetMap;
    if (l2LevelNoRemainBudget && canNotAssignBudget) {
      initBudgetMap = l2ObjectList.reduce((acc, l2Object) => {
        acc[l2Object.id] = l2Object.budget;
        return acc;
      }, {});
    } else {
      // first initialize budget with min(budget, spent)
      initBudgetMap = l2ObjectList.reduce((acc, l2Object) => {
        acc[l2Object.id] = increaseBudgetToTarget(
          0,
          Math.min(spentMap[l2Object.id], l2Object.budget)
        );
        return acc;
      }, {});

      // increase budget to spent if has remain budget
      l2ObjectList.forEach((l2Object) => {
        initBudgetMap[l2Object.id] = increaseBudgetToTarget(
          initBudgetMap[l2Object.id],
          spentMap[l2Object.id]
        );
      });

      // increase budget to minimum required budget if has remain budget
      if (totalBudget > 0) {
        let minL2ObjectBudget = order.campaignConstraint.campaignBudgetMinimum;
        const l2ObjectLessThanMinBudget = l2ObjectList.filter(
          (l2Object) => initBudgetMap[l2Object.id] < minL2ObjectBudget
        );
        let remainBudget =
          totalBudget +
          l2ObjectLessThanMinBudget.reduce(
            (acc, l2Object) => acc + initBudgetMap[l2Object.id],
            0
          );
        let allBudgetToAssign =
          minL2ObjectBudget * l2ObjectLessThanMinBudget.length;
        if (remainBudget < allBudgetToAssign) {
          minL2ObjectBudget = getPriceValue(
            l1Object.currency,
            remainBudget / l2ObjectLessThanMinBudget.length
          );
          allBudgetToAssign = remainBudget;
        }
        l2ObjectLessThanMinBudget.forEach((l2Object, index) => {
          const lastOne = index === l2ObjectLessThanMinBudget.length - 1;
          initBudgetMap[l2Object.id] = lastOne
            ? allBudgetToAssign
            : minL2ObjectBudget;
          allBudgetToAssign -= minL2ObjectBudget;
        });
      }
    }

    const viewData = l2ObjectList.map((l2Object) => {
      const budget = _.defaultTo(
        budgetDataMap[l2Object.id],
        getPriceValue(l1Object.currency, initBudgetMap[l2Object.id])
      );
      budgetDataMap[l2Object.id] = budget;
      return {
        channel: ChannelGroupSetting.RTB.key,
        id: l2Object.id,
        name: l2Object.name,
        status: getRtbCampaignStatusDesData(_.get(l2Object, 'state')),
        spent: spentMap[l2Object.id],
        budget,
        minBudget: 0
      };
    });
    return {
      viewData,
      budgets: budgetDataMap
    };
  } catch (e) {
    return {
      viewData: [],
      budgets: {}
    };
  }
}
